import { Button, FoodsIllustrations, Typography, WhiteBox } from "@foodpilot/foods";
import { Stack, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type ErrorPageKind = "accessDenied" | "notFound" | "serviceUnavailable" | "unexpected";

type ErrorPageProps = {
    kind: ErrorPageKind;
    description?: ReactNode;
    onContactSupport?: () => void;
    onGoHome?: () => void;
    onRetry?: () => void;
};

export const ErrorPage = (props: ErrorPageProps) => {
    const { kind, description, onContactSupport, onGoHome, onRetry } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const content = {
        accessDenied: {
            title: t("error_page.access_denied.title", "You don’t have access to this page"),
            description: t("error_page.access_denied.description", "Contact an administrator to get access."),
            illustration: "noAccess" as const,
        },
        notFound: {
            title: t("error_page.not_found.title", "This page doesn’t exist"),
            description: t(
                "error_page.not_found.description",
                "The link may be wrong, or the page may have been moved or deleted.",
            ),
            illustration: "underConstruction" as const,
        },
        serviceUnavailable: {
            title: t("error_page.service_unavailable.title", "The platform is temporarily unavailable"),
            description: t(
                "error_page.service_unavailable.description",
                "We couldn’t load this page. Please try again in a few moments.",
            ),
            illustration: "underConstruction" as const,
        },
        unexpected: {
            title: t("error_page.unexpected.title", "Something went wrong"),
            description: t(
                "error_page.unexpected.description",
                "An unexpected error occurred while loading this page. Try again, or contact support if the problem persists.",
            ),
            illustration: "contactSupport" as const,
        },
    }[kind];

    return (
        <WhiteBox sx={{ width: "100%" }}>
            <Stack alignItems="center" gap={theme.spacing(4)} padding={theme.spacing(4)} textAlign="center">
                <FoodsIllustrations illustration={content.illustration} />
                <Stack alignItems="center" gap={theme.spacing(1)}>
                    <Typography component="h1" variant="h3">
                        {content.title}
                    </Typography>
                    <Typography variant="body">{description ?? content.description}</Typography>
                </Stack>
                {(onRetry !== undefined || onGoHome !== undefined || onContactSupport !== undefined) && (
                    <Stack direction="row" flexWrap="wrap" gap={theme.spacing(2)} justifyContent="center">
                        {onRetry !== undefined && (
                            <Button variant="primary" onClick={onRetry}>
                                {t("error_page.actions.try_again", "Try again")}
                            </Button>
                        )}
                        {onGoHome !== undefined && (
                            <Button variant={onRetry === undefined ? "primary" : "secondary"} onClick={onGoHome}>
                                {t("error_page.actions.go_home", "Go to home page")}
                            </Button>
                        )}
                        {onContactSupport !== undefined && (
                            <Button variant="secondary" onClick={onContactSupport}>
                                {t("error_page.actions.contact_support", "Contact support")}
                            </Button>
                        )}
                    </Stack>
                )}
            </Stack>
        </WhiteBox>
    );
};
